/**
 * useRoleContext — manages the context pill state (Self / My Team / Admin)
 * and derives effective role per app based on app_role_overrides.
 *
 * PILL_MAP maps role → { labels[], ctxs[] }
 *   - labels: display text for each pill button
 *   - ctxs:   internal context keys  ('self' | 'team' | 'admin' | 'exec')
 *
 * All role + app-access assignments come from the server (HRMS DB).
 * Configure via:
 *   HRMS → Admin → App Access    (which apps each user can see in the switcher)
 *   HRMS → Admin → Access Control  (which modules within each app)
 *
 * Permanent privileged users (sabary@ebekindia.com, accounts@ebekindia.com,
 * md@ebekindia.com) are set as superadmin/executive in the DB.
 */

import { useState } from "react";

// ─── Pill definitions per role ────────────────────────────────────────────────
// NOTE: "admin" role has been removed from the system — all former admins are
// now "manager". The "Super Admin" pill is reserved for superadmin only.
export const PILL_MAP = {
  superadmin:  { labels: ["Self", "My Team", "Super Admin"], ctxs: ["self", "team", "admin"] },
  manager:     { labels: ["Self", "My Team"],                ctxs: ["self", "team"] },
  team_leader: { labels: ["Self", "My Team"],                ctxs: ["self", "team"] },
  employee:    { labels: [],                                 ctxs: ["self"] },
  executive:   { labels: [],                                 ctxs: ["exec"] },
};

/**
 * @param {object} user   - auth user object ({ role, email, company, … })
 * @param {'hrms'|'crm'|'expense'} appKey - which app we're in
 * @returns {{ context, setContext, effectiveRole, pillDef, allowedApps }}
 */
export function useRoleContext(user, appKey) {
  const email = user?.email ?? "";
  const baseRole = user?.role ?? "employee";

  // Effective role comes from server-stored app_role_overrides (set via App Access Manager).
  // Falls back to the user's base role if no per-app override is set.
  const effectiveRole = user?.app_role_overrides?.[appKey] ?? baseRole;

  const pillDef = PILL_MAP[effectiveRole] || PILL_MAP.employee;
  // Everyone starts on the FIRST pill ("Self" for superadmin/manager/TL) —
  // they can switch to My Team / Admin via the context pills.
  const defaultCtx = pillDef.ctxs[0];

  // Persist selected context per-app per-user across refreshes
  const storageKey = `role_ctx_${appKey}_${email}`;
  const [context, setContextRaw] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return pillDef.ctxs.includes(saved) ? saved : defaultCtx;
  });

  const setContext = (ctx) => {
    if (pillDef.ctxs.includes(ctx)) {
      localStorage.setItem(storageKey, ctx);
      setContextRaw(ctx);
    }
  };

  // Which app buttons the user is allowed to see in the app switcher.
  // Priority: server-stored allowed_apps > hardcoded override.
  // Superadmins can always open every Ebek One workspace, including SimOps.
  const allowedApps = (() => {
    if (effectiveRole === "superadmin") return ["hrms", "crm", "expense", "simops"];
    const serverApps = user?.allowed_apps;
    if (serverApps) {
      return Array.isArray(serverApps) ? serverApps : JSON.parse(serverApps);
    }
    // No explicit config → HRMS only (use App Access Manager to grant CRM/Expense)
    return ["hrms"];
  })();

  return { context, setContext, effectiveRole, pillDef, allowedApps };
}
